import type { ComponentType, ReactNode } from 'react';
import { ArrowUpRight, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export interface QuickAction {
  label: string;
  onClick: () => void;
  icon?: ComponentType<{ className?: string }>;
}

interface QuickAccessCardProps {
  title: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  /** Opens the page (the card face itself) */
  onOpen: () => void;
  /**
   * The first action is the main button; any others go in its dropdown.
   * Omitted = a plain "Open" button.
   */
  actions?: QuickAction[];
  /** Small live line under the description (counts, today's items, …) */
  meta?: ReactNode;
}

const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80';

/**
 * Compact quick access tile: a bold colored face (distinct from the light
 * status cards) with the page icon and a one-line purpose, and one action
 * at the bottom. Several actions become a split button: the main action,
 * plus a dropdown for the rest.
 */
export default function QuickAccessCard({
  title,
  description,
  icon: Icon,
  onOpen,
  actions,
  meta,
}: Readonly<QuickAccessCardProps>) {
  const [main, ...more] = actions?.length
    ? actions
    : [{ label: 'Open', onClick: onOpen }];
  const MainIcon = main.icon;

  return (
    <div
      className={`group relative flex min-w-0 flex-col overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 via-slate-700 to-slate-600 p-4 text-white shadow-md ring-1 ring-black/5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl`}
    >
      {/* Decorative oversized icon watermark */}
      <Icon
        aria-hidden
        className="pointer-events-none absolute -bottom-4 -right-4 h-20 w-20 text-white/10 transition-transform duration-300 group-hover:scale-110"
      />

      <button
        type="button"
        onClick={onOpen}
        className={`relative flex items-start gap-2.5 rounded-lg text-left ${FOCUS}`}
      >
        <span className="rounded-lg bg-white/20 p-2 backdrop-blur-sm">
          <Icon className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1 font-serif text-sm font-semibold">
            <span className="truncate">{title}</span>
            <ArrowUpRight className="h-3.5 w-3.5 shrink-0 opacity-70 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </span>
          <span
            className="mt-0.5 line-clamp-2 text-xs text-white/85"
            title={description}
          >
            {description}
          </span>
          {meta && (
            <span className="mt-0.5 block truncate text-xs font-medium text-white">
              {meta}
            </span>
          )}
        </span>
      </button>

      <div className="relative mt-auto flex pt-3">
        <button
          type="button"
          onClick={main.onClick}
          className={`inline-flex min-w-0 items-center gap-1 bg-white px-3 py-1 text-xs font-semibold text-slate-900 shadow-sm transition-colors hover:bg-white/90 ${FOCUS} ${more.length ? 'rounded-l-full' : 'rounded-full'}`}
        >
          {MainIcon && <MainIcon className="h-3.5 w-3.5 shrink-0" />}
          <span className="truncate">{main.label}</span>
        </button>
        {more.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={`More ${title} actions`}
                className={`inline-flex items-center rounded-r-full border-l border-slate-200 bg-white px-2 py-1 text-slate-700 shadow-sm transition-colors hover:bg-white/90 ${FOCUS}`}
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {more.map(({ label, onClick, icon: ItemIcon }) => (
                <DropdownMenuItem key={label} onSelect={onClick}>
                  {ItemIcon && <ItemIcon className="mr-2 h-4 w-4" />}
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
